import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Dialog, Flag, IconButton, Textarea, TextInput} from 'platform/components';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Show,
  Text,
  VStack,
  type ThemeIconKey,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {ReactNode} from 'react';

import {useGetBranchV2Query} from '@dms/api/base';
import {useGetVehicleQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {useCountry, useVehicleCatalogue} from '@dms/shared';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import {openingHoursSummary} from '../../model/openingHoursSummary';
import {sanitizeListingHtml} from '../../model/sanitizeListingHtml';
import {useAuctionFormat} from '../components/useAuctionFormat';
import {BuyerCertificate} from './BuyerCertificate';
import {ListingGallery} from './ListingVehicle';
import {useListingParameters, type ListingParameter} from './useListingParameters';

interface ListingPreviewDialogProps {
  isOpen: boolean;
  vehicleId: string;
  title: string;
  description: string | null;
  publicNote: string | null;
  biddingDays: number;
  decisionDays: number;
  estimatedAvailabilityAt: string | null;
  collectionSiteId: string | null;
  estimatedMileage: number | null;
  inspectionId: string | null;
  onClose: () => void;
}

function ListingHtml(props: {html: string | null}) {
  return (
    <Text>
      <div
        // eslint-disable-next-line eslint-js/no-restricted-syntax, react/no-danger -- Data is sanitized so it's safe to use dangerouslySetInnerHTML
        dangerouslySetInnerHTML={{__html: sanitizeListingHtml(props.html)}}
      />
    </Text>
  );
}

function PreviewCard(props: {title?: string; children: ReactNode}) {
  return (
    <Box
      padding={4}
      borderRadius="small"
      backgroundColor="palettes.white.10.100"
      boxShadow="elevation_1"
    >
      <VStack spacing={3}>
        <Show when={props.title}>
          <Heading size={4}>{props.title}</Heading>
        </Show>
        {props.children}
      </VStack>
    </Box>
  );
}

function SpecRow(props: ListingParameter) {
  return (
    <HStack spacing={2} align="flex-start" justify="space-between">
      <HStack spacing={2} align="center">
        <Icon value={props.icon} color="text.secondary" size={4} />
        <Text size="xSmall" color="secondary">
          {props.label}
        </Text>
      </HStack>
      <Text size="xSmall" alternative align="right">
        {props.value ?? EMPTY_PLACEHOLDER}
      </Text>
    </HStack>
  );
}

function ServiceBox(props: {title: string; text: string; action?: string}) {
  return (
    <Box padding={3} borderRadius="medium" backgroundColor="palettes.blue.10.100">
      <VStack spacing={2}>
        <Text size="small" alternative>
          {props.title}
        </Text>
        <Text size="xSmall" color="secondary">
          {props.text}
        </Text>
        <Show when={props.action}>
          {(action) => <Button variant="secondary" size="small" title={action} isDisabled />}
        </Show>
      </VStack>
    </Box>
  );
}

function ContactRow(props: {icon: ThemeIconKey; value: string}) {
  return (
    <HStack spacing={2} align="center">
      <Icon value={props.icon} color="text.secondary" size={4} />
      <Text size="small" color="secondary">
        {props.value}
      </Text>
    </HStack>
  );
}

function TermRow(props: {label: string; value: string}) {
  return (
    <HStack spacing={2} justify="space-between">
      <Text size="small" color="secondary">
        {props.label}
      </Text>
      <Text size="small" alternative>
        {props.value}
      </Text>
    </HStack>
  );
}

// All the technical specifications of the v11 buyer listing that the vehicle record has.
function useBuyerSpecs(
  vehicleId: string,
  estimatedAvailabilityAt: string | null,
  estimatedMileage: number | null
): ListingParameter[] {
  const {data: vehicle} = useGetVehicleQuery({vehicleId});
  const [catalogue] = useVehicleCatalogue(vehicle?.type);
  const {getCountryName} = useCountry();
  const format = useAuctionFormat();
  const formatNumber = useFormatNumber();
  const parameters = useListingParameters(vehicleId);
  const data = vehicle?.vehicle;
  const parameter = (icon: ThemeIconKey) => parameters.find((item) => item.icon === icon)?.value;
  const count = (value: number | null | undefined) =>
    value === null || value === undefined ? null : formatNumber(value);
  const withUnit = (value: number | null | undefined, unit: string) =>
    value === null || value === undefined ? null : `${formatNumber(value)} ${unit}`;
  const km = (value: number | null | undefined) => withUnit(value, i18n.t('general.metric.km'));
  const power = data?.power ?? vehicle?.power;
  const interior = [
    catalogue.getBodyColor(data?.interiorColor?.value) ?? data?.interiorColor?.name,
    catalogue.getInteriorMaterial(data?.interiorMaterial),
  ]
    .filter(Boolean)
    .join(' / ');

  return [
    {
      icon: 'editor/attach_money',
      label: i18n.t('entity.auction.listing.vatDeductible'),
      value: parameter('editor/attach_money'),
    },
    {icon: 'action/description', label: i18n.t('entity.vehicle.labels.vin'), value: vehicle?.vin},
    {
      icon: 'automotive/calendar',
      label: i18n.t('entity.auction.settings.estimatedAvailability'),
      value: estimatedAvailabilityAt ? format.date(estimatedAvailabilityAt) : null,
    },
    {
      icon: 'automotive/calendar',
      label: i18n.t('entity.vehicle.labels.firstRegistration'),
      value: parameter('specification/year'),
    },
    {icon: 'automotive/engine', label: i18n.t('entity.vehicle.labels.trim'), value: vehicle?.trim},
    {
      icon: 'communication/location_on',
      label: i18n.t('entity.vehicle.labels.firstRegistrationCountry'),
      value: getCountryName(data?.firstRegistrationCountry),
    },
    {
      icon: 'automotive/transmission',
      label: i18n.t('entity.auction.listing.gearbox'),
      value: parameter('specification/transmission'),
    },
    {
      icon: 'automotive/mileage',
      label: i18n.t('entity.auction.listing.mileage'),
      value: km(data?.mileage),
    },
    {
      icon: 'automotive/car',
      label: i18n.t('entity.vehicle.labels.bodyType'),
      value: catalogue.getBodyStyle(vehicle?.bodyStyle ?? data?.bodyStyle),
    },
    {
      icon: 'automotive/mileage',
      label: i18n.t('entity.auction.settings.estimatedMileage'),
      value: km(estimatedMileage),
    },
    {
      icon: 'automotive/transmission',
      label: i18n.t('entity.auction.listing.drive'),
      value: parameter('specification/drive_type'),
    },
    {
      icon: 'communication/vpn_key',
      label: i18n.t('entity.vehicle.labels.numberOfKeysFull'),
      value: count(data?.primaryKeyCount),
    },
    {
      icon: 'automotive/engine',
      label: i18n.t('entity.auction.listing.power'),
      value:
        power === null || power === undefined
          ? null
          : `${formatNumber(power)} ${i18n.t('general.metric.kW')} / ${formatNumber(
              Math.round(power * KW_TO_HP)
            )} ${i18n.t('general.metric.HP')}`,
    },
    {
      icon: 'communication/business',
      label: i18n.t('entity.vehicle.labels.previousOwners'),
      value: count(data?.ownerCount),
    },
    {
      icon: 'automotive/engine',
      label: i18n.t('entity.vehicle.labels.engineCapacity'),
      value: withUnit(data?.engineVolume, i18n.t('general.metric.ccm')),
    },
    {
      icon: 'automotive/car',
      label: i18n.t('entity.vehicle.labels.numberOfDoorsFull'),
      value: catalogue.getDoorCount(vehicle?.doorCountCategory ?? data?.doorCountCategory),
    },
    {
      icon: 'automotive/car',
      label: i18n.t('entity.vehicle.labels.bodyColor'),
      value: catalogue.getBodyColor(data?.exteriorColor?.value) ?? data?.exteriorColor?.name,
    },
    {
      icon: 'automotive/car',
      label: i18n.t('entity.vehicle.labels.numberOfSeatsFull'),
      value: count(data?.seatCount),
    },
    {
      icon: 'automotive/car',
      label: i18n.t('entity.auction.preview.interiorColorMaterial'),
      value: interior || null,
    },
    {
      icon: 'automotive/fuel_type',
      label: i18n.t('entity.auction.listing.fuel'),
      value: parameter('specification/fuel_type'),
    },
  ];
}

const KW_TO_HP = 1.35962;

// The listing as the buyers see it in the Fastback Trade auction (prototype v11 layout).
export function ListingPreviewDialog(props: ListingPreviewDialogProps) {
  const format = useAuctionFormat();
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const parameters = useListingParameters(props.vehicleId);
  const specs = useBuyerSpecs(
    props.vehicleId,
    props.estimatedAvailabilityAt,
    props.estimatedMileage
  );
  const {getCountryName} = useCountry();
  // The buyer collects the vehicle at the selected collection site, so it is the seller contact.
  const {currentData: collectionSite} = useGetBranchV2Query(
    props.collectionSiteId ? {id: props.collectionSiteId} : skipToken
  );
  const collectionHours = openingHoursSummary(collectionSite?.salesOpeningHours, (day) =>
    i18n.t(`general.labels.calendar.${day}`)
  );
  const address = collectionSite?.address;
  const pickupAddress = address
    ? [address.street, `${address.zip} ${address.city}`, getCountryName(address.country)]
        .filter(Boolean)
        .join(', ')
    : EMPTY_PLACEHOLDER;
  const contacts = collectionSite?.publicContacts;
  const phone = contacts?.phoneNumber
    ? `${contacts.phoneNumber.prefix} ${contacts.phoneNumber.number}`
    : null;
  const [catalogue] = useVehicleCatalogue(vehicle?.type);
  const equipment = vehicle?.features ?? [];
  // The badges under the title repeat the key parameters, as in v11.
  const badges = parameters
    .filter(({icon}) =>
      ['specification/year', 'specification/fuel_type', 'specification/transmission'].includes(icon)
    )
    .flatMap(({value}) => (value ? [value] : []));

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="xLarge"
      title={i18n.t('entity.auction.preview.title')}
      data-testid="auction-listing-preview"
    >
      <Box padding={4} borderRadius="small" backgroundColor="palettes.neutral.10.100">
        <Grid columns={[1, 1, 3]} spacing={4} align="flex-start">
          <GridItem span={[1, 1, 2]}>
            <VStack spacing={4}>
              <PreviewCard>
                <Heading size={2}>{props.title}</Heading>
                <HStack spacing={2} wrap>
                  {badges.map((badge) => (
                    <Flag key={badge} label={badge} colorScheme="neutral" isSubtle size="small" />
                  ))}
                  <Show when={vehicle?.isVatDeductible === false}>
                    <Flag
                      label={i18n.t('entity.auction.confirmation.vatNotDeductible')}
                      colorScheme="orange"
                      isSubtle
                      size="small"
                    />
                  </Show>
                </HStack>
                <ListingGallery vehicleId={props.vehicleId} />
              </PreviewCard>
              <PreviewCard title={i18n.t('entity.auction.preview.technicalSpecifications')}>
                <Grid columns={[1, 2]} spacing={4}>
                  {specs.map((spec) => (
                    <SpecRow key={spec.label} {...spec} />
                  ))}
                </Grid>
              </PreviewCard>
              <PreviewCard title={i18n.t('entity.auction.listing.description')}>
                <ListingHtml html={props.description} />
              </PreviewCard>
              <Show when={props.publicNote}>
                {(publicNote) => (
                  <PreviewCard title={i18n.t('entity.auction.preview.noteFromSeller')}>
                    <ListingHtml html={publicNote} />
                  </PreviewCard>
                )}
              </Show>
              <PreviewCard title={i18n.t('entity.auction.preview.equipment')}>
                {equipment.length > 0 ? (
                  <Grid columns={[1, 2]} spacing={2}>
                    {equipment.map((feature) => (
                      <HStack key={feature.key} spacing={2} align="center">
                        <Icon value="navigation/check" color="text.secondary" size={4} />
                        <Text size="small" color="secondary">
                          {catalogue.getFeature(feature.key) ?? feature.translation}
                        </Text>
                      </HStack>
                    ))}
                  </Grid>
                ) : (
                  <Text size="small" color="secondary">
                    {i18n.t('entity.auction.preview.noEquipment')}
                  </Text>
                )}
              </PreviewCard>
              <BuyerCertificate inspectionId={props.inspectionId} />
            </VStack>
          </GridItem>
          <VStack spacing={4}>
            <PreviewCard title={i18n.t('entity.auction.preview.offerDetails')}>
              <TextInput
                label={i18n.t('entity.auction.preview.priceOffer')}
                value={null}
                onChange={() => undefined}
                placeholder="0"
                isDisabled
              />
              <Button
                variant="primary"
                title={i18n.t('entity.auction.preview.makeOffer')}
                isDisabled
                isFullWidth
              />
              <Text size="xSmall" color="secondary" align="center">
                {i18n.t('entity.auction.preview.offersDisabled')}
              </Text>
            </PreviewCard>
            {/* shortcut: a static mockup of the buyer chat, connect it when the marketplace chat exists. */}
            <PreviewCard title={collectionSite?.name ?? EMPTY_PLACEHOLDER}>
              <Show when={collectionSite?.companyName}>
                {(companyName) => (
                  <Text size="small" color="secondary">
                    {companyName}
                  </Text>
                )}
              </Show>
              <ContactRow icon="communication/location_on" value={pickupAddress} />
              <Show when={phone}>
                {(value) => <ContactRow icon="communication/phone" value={value} />}
              </Show>
              <Show when={contacts?.emailAddress}>
                {(value) => <ContactRow icon="communication/email" value={value} />}
              </Show>
              <HStack spacing={2} align="center">
                <Box flex={1}>
                  <Textarea
                    value={null}
                    onChange={() => undefined}
                    placeholder={i18n.t('entity.auction.preview.sendMessage')}
                    rows={1}
                    isDisabled
                  />
                </Box>
                <IconButton icon="content/send" priority="secondary" isDisabled />
              </HStack>
            </PreviewCard>
            <PreviewCard title={i18n.t('entity.auction.preview.services')}>
              <ServiceBox
                title={i18n.t('entity.auction.preview.cebiaTitle')}
                text={i18n.t('entity.auction.preview.cebiaText')}
                action={i18n.t('entity.auction.preview.cebiaAction')}
              />
              <ServiceBox
                title={i18n.t('entity.auction.preview.priceReportTitle')}
                text={i18n.t('entity.auction.preview.priceReportText')}
                action={i18n.t('entity.auction.preview.priceReportAction')}
              />
              <ServiceBox
                title={i18n.t('entity.auction.preview.transConnectTitle')}
                text={i18n.t('entity.auction.preview.transConnectText')}
              />
            </PreviewCard>
            <PreviewCard title={i18n.t('entity.auction.preview.availability')}>
              <TermRow
                label={i18n.t('entity.auction.preview.auctionDuration')}
                value={i18n.t('entity.auction.settings.days', {count: props.biddingDays})}
              />
              <TermRow
                label={i18n.t('entity.auction.settings.decisionWindow')}
                value={i18n.t('entity.auction.preview.decisionWindowValue', {
                  days: i18n.t('entity.auction.settings.days', {count: props.decisionDays}),
                })}
              />
              <TermRow
                label={i18n.t('entity.auction.settings.estimatedAvailability')}
                value={
                  props.estimatedAvailabilityAt
                    ? format.date(props.estimatedAvailabilityAt)
                    : EMPTY_PLACEHOLDER
                }
              />
              <TermRow
                label={i18n.t('entity.auction.settings.collectionSite')}
                value={collectionSite?.name ?? EMPTY_PLACEHOLDER}
              />
              <TermRow
                label={i18n.t('entity.auction.preview.collectionHours')}
                value={collectionHours ?? EMPTY_PLACEHOLDER}
              />
            </PreviewCard>
          </VStack>
        </Grid>
      </Box>
    </Dialog>
  );
}
